import { LegacySpinner } from '@/ui/LegacySpinner';
import { useMfeMountStore } from './mfe-mount-store';
import { useClientStore } from '@/client/client-store';

/**
 * Full-viewport blocking overlay shown while the shell's primary MFE is loading
 * (Bug #8). At `z-index: 9999` it sits above the header (8) and nav rail (7), so
 * clicks on rail items are swallowed and the user can't kick off a second app
 * load before the current one finishes bootstrapping (the historic race that
 * left the new app half-mounted). Mirrors the legacy full-shell blur loader.
 */
export function MfeMountOverlay() {
  const loading = useMfeMountStore((s) => s.loading);
  const appId = useMfeMountStore((s) => s.appId);
  // A direct/hard-URL client change resolves this tab to the new client and the
  // new client's home MFE begins loading immediately. If this z-9999 blocking
  // overlay stays up it hides the `DirectClientChangeGuard` "Client Change"
  // confirmation (a MotifModal that stacks below 9999), so the dialog only
  // surfaced once the MFE finished loading ("appears after landing"). Suppress
  // the overlay while a switch confirmation is pending so the dialog shows right
  // away — the MotifModal renders its own backdrop over the content beneath.
  const clientSwitchPending = useClientStore((s) => s.pendingDirectSwitch !== null);

  if (!loading || clientSwitchPending) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={`Loading ${appId}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <LegacySpinner />
    </div>
  );
}
